/**
 * AG-2415 — switch organisation without signing out.
 *
 * The control itself is `TenancySwitcher` from `@vaarta/suite-launcher`, the same bytes in every
 * suite application. This file supplies the three things only Analytics knows: how to reach its
 * own backend (`/v1/auth/tenancies`, `/v1/auth/switch-tenancy`, both relays over Identity Studio
 * that need this application's SDK secret, so a browser cannot call Identity Studio directly),
 * and what to do with a switched session — exactly what a completed sign-in does with one.
 *
 * ⚠️ The switcher draws NOTHING for somebody with a single organisation, which is most people.
 */
import { TenancySwitcher, type RelayAnswer } from '@vaarta/suite-launcher';

import { useAuth } from '../../../hooks/useAuth';
import { ApiError, apiFetch, type ApiFetchOptions } from '../../../lib/api';

export const TENANCIES_PATH = '/v1/auth/tenancies';
export const SWITCH_TENANCY_PATH = '/v1/auth/switch-tenancy';

/**
 * One call to this application's own backend, as the switcher reads answers: `{status, body}`.
 *
 * ⛔ An HTTP refusal is an ANSWER, not an exception: a 403 (`not_your_tenancy`) or a 409
 * (`already_in_tenancy`) means something specific to the person, and the switcher can only say
 * so if it sees the status and the body. Only a request that never got an HTTP answer (offline,
 * aborted — `ApiError` status 0) is thrown, and the switcher says it could not switch.
 */
export async function relay(path: string, init: ApiFetchOptions = {}): Promise<RelayAnswer> {
  try {
    return { status: 200, body: await apiFetch<unknown>(path, init) };
  } catch (error) {
    if (error instanceof ApiError && error.status > 0) {
      return { status: error.status, body: error.payload };
    }
    throw error;
  }
}

export function loadTenancies(): Promise<RelayAnswer> {
  return relay(TENANCIES_PATH);
}

export function switchTenancy(applicationTenantId: string): Promise<RelayAnswer> {
  return relay(SWITCH_TENANCY_PATH, {
    method: 'POST',
    body: JSON.stringify({ application_tenant_id: applicationTenantId }),
  });
}

export function OrganizationSwitcher() {
  const { user, config, open, adoptRedeemed } = useAuth();

  // Signed out, or a deployment that does not authenticate: there is no organisation to switch.
  if (!user || open || !config?.enabled) return null;

  const onSwitched = async (bundle: unknown) => {
    // ⭐ The answer IS a sign-in answer (the backend ends the switch on its sign-in tail), so it
    // is applied by the same `adopt` every way in uses. NOT `adoptAndPlant`: a switch carries
    // no suite cookie to collect — the suite sign-in is untouched by design. Then reload, so
    // nothing fetched in the old organisation survives on screen.
    adoptRedeemed(bundle as Parameters<typeof adoptRedeemed>[0]);
    window.location.reload();
  };

  return (
    <TenancySwitcher
      loadTenancies={loadTenancies}
      switchTenancy={switchTenancy}
      onSwitched={onSwitched}
      cacheKey={`analytics:${user.id}`}
    />
  );
}
